import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@/store';
import { useCreateProfile, queryKeys } from '@/hooks/api';
import { Button, LoadingSpinner } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { api } from '@/api';
import type { TestQuestion, TestAnswer, BadgeLevel } from '@/types';

type LoadingPhase = 'checking' | 'generating' | 'ready' | 'submitting' | 'evaluating' | 'completed' | 'error';

interface ITQuestScreenProps {
  mode?: 'onboarding' | 'retest';
  specializationId?: number;
  declaredLevel?: string;
}

const ITQuestScreen: React.FC<ITQuestScreenProps> = ({
  mode: propMode,
  specializationId: propSpecializationId,
  declaredLevel: propDeclaredLevel,
}) => {
  const { updateOnboardingData, onboarding, updateUser, setActiveScreen, setOnboardingCompleted, ui, clearITQuestParams, user } = useAppStore();
  const createProfile = useCreateProfile();
  const queryClient = useQueryClient();

  // Determine mode: use prop or check itQuestParams from store or default to onboarding
  const mode = propMode || ui.itQuestParams?.mode || 'onboarding';

  // State
  const [phase, setPhase] = useState<LoadingPhase>('checking');
  const [testId, setTestId] = useState<number | null>(null);
  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [currentAnswer, setCurrentAnswer] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Results
  const [score, setScore] = useState<number | null>(null);
  const [badgeLevel, setBadgeLevel] = useState<BadgeLevel | null>(null);

  // Get specialization: from props, itQuestParams, onboarding data, OR USER PROFILE
  const specializationId = propSpecializationId ||
    ui.itQuestParams?.specializationId ||
    (onboarding.data as any)?.specializationId ||
    user?.specializations?.[0]?.id; // БЕРЕМ ИЗ ПРОФИЛЯ!

  const declaredLevel = propDeclaredLevel ||
    ui.itQuestParams?.declaredLevel ||
    (onboarding.data as any)?.declaredLevel ||
    user?.level?.toLowerCase() ||
    'senior';

  // Log only once on mount
  useEffect(() => {
    console.log('🎯 ITQuestScreen mounted:', {
      mode,
      specializationId,
      declaredLevel,
      source: propSpecializationId ? 'props' :
        ui.itQuestParams?.specializationId ? 'itQuestParams' :
          (onboarding.data as any)?.specializationId ? 'onboarding.data' :
            user?.specializations?.[0]?.id ? 'USER PROFILE' : 'none',
    });

    initializeTest();
  }, []); // Empty deps - run only once

  const initializeTest = async () => {
    try {
      setPhase('checking');
      setError(null);

      // Validate that we have specializationId
      if (!specializationId) {
        console.error('❌ No specializationId provided');
        setError('Специализация не выбрана. Вернитесь назад и выберите специализацию.');
        setPhase('error');
        return;
      }

      console.log('✅ Starting test with:', { specializationId, declaredLevel });

      // Step 1: Check verification status
      const status = await api.getVerificationStatus();

      if (status.has_active_test && status.active_test_id) {
        // Resume existing test
        setTestId(status.active_test_id);

        if (status.active_test_status === 'ready') {
          // Questions are already ready
          await fetchTestQuestions(status.active_test_id);
        } else if (status.active_test_status === 'generating') {
          // Still generating, start polling
          setPhase('generating');
          await pollForQuestions(status.active_test_id);
        } else if (status.active_test_status === 'evaluating') {
          // Still evaluating, poll for results
          setPhase('evaluating');
          await pollForResults(status.active_test_id);
        } else if (status.active_test_status === 'pending') {
          // Test created but not started, begin generation
          setPhase('generating');
          await pollForQuestions(status.active_test_id);
        }
      } else {
        // Start new test
        await startNewTest();
      }
    } catch (err: any) {
      console.error('Failed to initialize test:', err);
      setError(err.message || 'Failed to initialize test');
      setPhase('error');
      TelegramUtils.haptic.error();
    }
  };

  const startNewTest = async () => {
    try {
      setPhase('generating');
      setLoadingProgress(0);

      console.log('📝 Starting new test with params:', {
        specialization_id: specializationId,
        declared_level: declaredLevel,
      });

      // Start test generation
      const startResponse = await api.startVerificationTest({
        specialization_id: specializationId,
        declared_level: declaredLevel,
      });

      setTestId(startResponse.test_id);

      // Poll for questions
      await pollForQuestions(startResponse.test_id);
    } catch (err: any) {
      console.error('Failed to start test:', err);
      setError(err.message || 'Failed to start test');
      setPhase('error');
      TelegramUtils.haptic.error();
    }
  };

  const pollForQuestions = async (id: number) => {
    try {
      setPhase('generating');

      // Start smooth progress animation to 95% over 30 seconds
      const progressInterval = setInterval(() => {
        setLoadingProgress((prev) => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          // Increment to reach 95% in 30 seconds (95 / 30000ms * 100ms)
          return prev + (95 / 300);
        });
      }, 100); // Update every 100ms

      const testData = await api.waitForTestQuestions(
        id,
        () => { }, // No callback needed, using timer instead
        60, // max attempts
        3000 // 3 seconds interval
      );

      clearInterval(progressInterval);

      if (testData.questions && testData.questions.length > 0) {
        setQuestions(testData.questions);
        setPhase('ready');
        setLoadingProgress(100);
        TelegramUtils.haptic.success();
      } else {
        throw new Error('No questions received');
      }
    } catch (err: any) {
      console.error('Failed to get questions:', err);
      setError(err.message || 'Failed to generate questions. Please try again.');
      setPhase('error');
      TelegramUtils.haptic.error();
    }
  };

  const fetchTestQuestions = async (id: number) => {
    try {
      const testData = await api.getTestData(id);

      if (testData.status === 'ready' && testData.questions) {
        setQuestions(testData.questions);
        setPhase('ready');
        TelegramUtils.haptic.success();
      } else if (testData.status === 'generating') {
        setPhase('generating');
        await pollForQuestions(id);
      } else {
        throw new Error('Test is not ready');
      }
    } catch (err: any) {
      console.error('Failed to fetch questions:', err);
      setError(err.message || 'Failed to fetch questions');
      setPhase('error');
    }
  };

  const handleAnswerChange = (value: string) => {
    setCurrentAnswer(value);
  };

  const handleNext = () => {
    const question = questions[currentQuestionIndex];

    if (!currentAnswer.trim()) {
      TelegramUtils.haptic.error();
      TelegramUtils.showAlert('Пожалуйста, ответьте на вопрос');
      return;
    }

    // Save answer
    setAnswers(prev => ({
      ...prev,
      [question.id]: currentAnswer,
    }));

    TelegramUtils.haptic.light();

    // Check if last question
    if (currentQuestionIndex === questions.length - 1) {
      // Submit answers
      submitAnswers({
        ...answers,
        [question.id]: currentAnswer,
      });
    } else {
      // Move to next question
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer('');
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      const prevQuestion = questions[currentQuestionIndex - 1];
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setCurrentAnswer(answers[prevQuestion.id] || '');
      TelegramUtils.haptic.light();
    }
  };

  const submitAnswers = async (allAnswers: Record<number, string>) => {
    if (!testId) return;

    try {
      setPhase('submitting');
      setLoadingProgress(0);

      // Format answers for API
      const formattedAnswers: TestAnswer[] = questions.map(q => ({
        question_id: q.id,
        answer: allAnswers[q.id] || '',
      }));

      // Submit answers
      await api.submitTestAnswers({
        test_id: testId,
        answers: formattedAnswers,
      });

      // Poll for results
      await pollForResults(testId);
    } catch (err: any) {
      console.error('Failed to submit answers:', err);
      setError(err.message || 'Failed to submit answers');
      setPhase('error');
      TelegramUtils.haptic.error();
    }
  };

  const pollForResults = async (id: number) => {
    try {
      setPhase('evaluating');

      // Start smooth progress animation to 95% over 30 seconds
      const progressInterval = setInterval(() => {
        setLoadingProgress((prev) => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          // Increment to reach 95% in 30 seconds (95 / 30000ms * 100ms)
          return prev + (95 / 300);
        });
      }, 100); // Update every 100ms

      const result = await api.waitForTestResult(
        id,
        () => { }, // No callback needed, using timer instead
        40, // max attempts
        3000 // 3 seconds interval
      );

      clearInterval(progressInterval);

      if (result.score !== undefined && result.badge_level) {
        setScore(result.score);
        setBadgeLevel(result.badge_level);
        setPhase('completed');
        setLoadingProgress(100);
        TelegramUtils.haptic.success();
      } else {
        throw new Error('Invalid result received');
      }
    } catch (err: any) {
      console.error('Failed to get results:', err);
      setError(err.message || 'Failed to evaluate test. Please try again.');
      setPhase('error');
      TelegramUtils.haptic.error();
    }
  };

  const handleComplete = async () => {
    try {
      if (score === null) return;

      // Update user profile with verification data
      // Capitalize badge level to match FullProfile type
      const capitalizedLevel = badgeLevel ?
        (badgeLevel.charAt(0).toUpperCase() + badgeLevel.slice(1)) as 'Junior' | 'Middle' | 'Senior' | 'Expert' :
        undefined;

      updateUser({
        itTestScore: score,
        verified: true,
        level: capitalizedLevel,
      });

      if (mode === 'retest') {
        // Retest mode: just reload profile and go back
        await queryClient.invalidateQueries({ queryKey: queryKeys.myProfile });

        // Clear IT Quest params
        clearITQuestParams();

        TelegramUtils.haptic.success();
        await TelegramUtils.showAlert(`Тест пройден! Ваш балл: ${score}`);

        // Go to skills verification screen
        setActiveScreen('skills-verification');
      } else {
        // Onboarding mode: complete profile creation
        updateOnboardingData({
          itTestScore: score,
          itTestCompleted: true,
        });

        // Complete profile creation
        const completeData = {
          ...onboarding.data,
          itTestScore: score,
          itTestCompleted: true,
        };

        await createProfile.mutateAsync(completeData as any);

        // Complete onboarding and navigate to feed
        setOnboardingCompleted(true);
        TelegramUtils.haptic.success();
        setActiveScreen('feed');
      }
    } catch (err: any) {
      console.error('Failed to complete:', err);
      setError(err.message || 'Failed to complete');
      TelegramUtils.haptic.error();
    }
  };

  const handleRetry = () => {
    setError(null);
    initializeTest();
  };

  // Configure back button
  useEffect(() => {
    if (phase === 'ready') {
      TelegramUtils.configureBackButton(currentQuestionIndex > 0, handleBack);
    } else {
      TelegramUtils.configureBackButton(false);
    }
  }, [phase, currentQuestionIndex]);

  // Render loading screen
  if (phase === 'checking' || phase === 'generating' || phase === 'submitting' || phase === 'evaluating') {
    const loadingConfig = {
      checking: {
        title: 'Инициализация',
        subtitle: 'Проверяем ваш статус верификации',
        type: 'simple' as const,
      },
      generating: {
        title: 'Генерация теста',
        subtitle: 'AI создает персонализированные вопросы',
        type: 'sparkle' as const,
      },
      submitting: {
        title: 'Отправка ответов',
        subtitle: 'Сохраняем ваши ответы',
        type: 'simple' as const,
      },
      evaluating: {
        title: 'Оценка результатов',
        subtitle: 'AI анализирует ваши ответы',
        type: 'brain' as const,
      },
    };

    const config = loadingConfig[phase as keyof typeof loadingConfig];

    return (
      <div className="min-h-screen bg-tg-bg p-6 flex flex-col items-center justify-center safe-area-y">
        <div className="w-full max-w-sm">
          {/* Animated Icon */}
          <div className="flex justify-center mb-8">
            {config.type === 'brain' ? (
              // Brain/AI analysis animation
              <div className="relative w-24 h-24">
                {/* Multiple rotating rings */}
                <div className="absolute inset-0 border-2 border-white/20 rounded-full animate-spin"
                  style={{ animationDuration: '3s' }}
                />
                <div className="absolute inset-2 border-2 border-white/15 rounded-full animate-spin"
                  style={{ animationDuration: '2s', animationDirection: 'reverse' }}
                />
                <div className="absolute inset-4 border-2 border-white/10 rounded-full animate-spin"
                  style={{ animationDuration: '1.5s' }}
                />
                {/* Center dot */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-full" />
                </div>
                {/* Orbiting particles */}
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '4s' }}>
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white rounded-full" />
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s', animationDelay: '0.5s' }}>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white rounded-full" />
                </div>
              </div>
            ) : config.type === 'sparkle' ? (
              // Brain/AI animation for generation (same as evaluation)
              <div className="relative w-24 h-24">
                {/* Multiple rotating rings */}
                <div className="absolute inset-0 border-2 border-white/20 rounded-full animate-spin"
                  style={{ animationDuration: '3s' }}
                />
                <div className="absolute inset-2 border-2 border-white/15 rounded-full animate-spin"
                  style={{ animationDuration: '2s', animationDirection: 'reverse' }}
                />
                <div className="absolute inset-4 border-2 border-white/10 rounded-full animate-spin"
                  style={{ animationDuration: '1.5s' }}
                />
                {/* Center dot */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-full" />
                </div>
                {/* Orbiting particles */}
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '4s' }}>
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white rounded-full" />
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s', animationDelay: '0.5s' }}>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white rounded-full" />
                </div>
              </div>
            ) : (
              // Simple spinner
              <div className="relative w-24 h-24">
                <div className="absolute inset-0 border-2 border-white/10 rounded-full animate-spin"
                  style={{ animationDuration: '3s' }}
                />
                <div className="w-24 h-24 bg-white/5 rounded-full" />
              </div>
            )}
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white text-center mb-2">
            {config.title}
          </h2>

          {/* Subtitle */}
          <p className="text-neutral-400 text-center mb-8">
            {config.subtitle}
          </p>

          {/* Progress Bar */}
          {loadingProgress > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-neutral-500 uppercase tracking-wider">
                  Прогресс
                </span>
                <span className="text-xs text-white font-mono">
                  {Math.round(loadingProgress)}%
                </span>
              </div>
              <div className="relative w-full h-1 bg-white/5 rounded-full overflow-hidden">
                {/* Background shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
                {/* Progress fill */}
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-white to-neutral-300 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${loadingProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Time estimate */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
            <p className="text-xs text-neutral-500 mb-1">
              Примерное время ожидания
            </p>
            <p className="text-sm text-white font-medium">
              {phase === 'generating' || phase === 'evaluating' ? '10-30 секунд' : '2-5 секунд'}
            </p>
          </div>

          {/* Fun fact or tip */}
          {(phase === 'generating' || phase === 'evaluating') && (
            <div className="mt-6 text-center">
              <p className="text-xs text-neutral-600">
                💡 {phase === 'generating'
                  ? 'AI адаптирует вопросы под ваш уровень и специализацию'
                  : 'AI оценивает не только правильность, но и глубину понимания'
                }
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render error screen
  if (phase === 'error' && error) {
    return (
      <div className="min-h-screen bg-tg-bg p-6 flex flex-col items-center justify-center safe-area-y">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-error-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>

          <h2 className="text-xl font-bold text-white mb-3">
            Ошибка
          </h2>

          <p className="text-sm text-neutral-400 mb-8">
            {error}
          </p>

          <Button
            onClick={handleRetry}
            className="w-full h-14 bg-white text-black hover:bg-neutral-100 font-bold text-base rounded-2xl"
          >
            Попробовать снова
          </Button>
        </div>
      </div>
    );
  }

  // Render results screen
  if (phase === 'completed' && score !== null && badgeLevel) {
    const scoreInfo = getScoreInfo(score, badgeLevel);

    return (
      <div className="min-h-screen bg-tg-bg p-6 flex flex-col items-center justify-center safe-area-y">
        <div className="w-full max-w-sm text-center">
          {/* Score Circle */}
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-32 h-32 border-4 border-white rounded-full mb-4">
              <div className="text-5xl font-bold text-white">{score}</div>
            </div>
          </div>

          {/* Level Badge */}
          <div className="mb-6">
            <span className={`inline-block px-4 py-2 rounded-full bg-white/10 ${scoreInfo.color} font-bold text-lg capitalize`}>
              {badgeLevel}
            </span>
          </div>

          {/* Description */}
          <h2 className="text-2xl font-bold text-white mb-3">
            Навыки подтверждены! ✓
          </h2>
          <p className="text-neutral-400 mb-8">
            {scoreInfo.description}
          </p>

          {/* Benefits */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-8 text-left">
            <h3 className="text-sm font-bold text-white mb-3 uppercase tracking-wider">
              Ваши преимущества:
            </h3>
            <div className="space-y-2 text-sm text-neutral-400">
              <div className="flex items-center gap-2">
                <span className="text-success-400">✓</span>
                <span>Verified badge в профиле</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-success-400">✓</span>
                <span>Балл {score}/100 виден всем</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-success-400">✓</span>
                <span>Приоритет в показе профиля</span>
              </div>
            </div>
          </div>

          {/* Complete Button */}
          <Button
            onClick={handleComplete}
            loading={createProfile.isPending}
            className="w-full h-14 bg-white text-black hover:bg-neutral-100 font-bold text-base rounded-2xl"
          >
            {mode === 'retest' ? 'В профиль' : 'Завершить регистрацию'}
          </Button>
        </div>
      </div>
    );
  }

  // Render questions screen
  if (phase === 'ready' && questions.length > 0) {
    const question = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
    const isLastQuestion = currentQuestionIndex === questions.length - 1;

    return (
      <div className="h-screen flex flex-col bg-tg-bg safe-area-y">
        {/* Header */}
        <div className="flex-shrink-0 px-6 pt-6 pb-2">
          <h1 className="text-2xl font-bold text-white mb-2">
            IT-Quest
          </h1>
          <p className="text-sm text-neutral-400">
            Тест на верификацию навыков • {questions.length} вопросов
          </p>
        </div>

        {/* Progress */}
        <div className="flex-shrink-0 px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-neutral-500">
              Вопрос {currentQuestionIndex + 1} из {questions.length}
            </span>
            <span className="text-sm text-neutral-500">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="w-full bg-neutral-900 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-white h-1.5 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 min-h-0">
          <div className="mb-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h2 className="text-lg font-medium text-white mb-6 leading-relaxed whitespace-pre-wrap">
                {question.question}
              </h2>

              {(question.type === 'multiple_choice' || question.type === 'single_choice') && question.options ? (
                <div className="space-y-3">
                  {question.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswerChange(option)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${currentAnswer === option
                          ? 'border-white bg-white/10 text-white light:border-neutral-800 light:bg-neutral-200 light:text-neutral-900'
                          : 'border-white/20 bg-white/5 text-neutral-400 hover:border-white/40 light:border-neutral-300 light:bg-neutral-100 light:text-neutral-700 light:hover:border-neutral-400'
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${currentAnswer === option
                            ? 'border-white bg-white light:border-neutral-800 light:bg-neutral-800'
                            : 'border-white/40 light:border-neutral-400'
                          }`}>
                          {currentAnswer === option && (
                            <div className="w-2.5 h-2.5 bg-black rounded-full light:bg-white" />
                          )}
                        </div>
                        <span className="flex-1">{option}</span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <textarea
                  value={currentAnswer}
                  onChange={(e) => handleAnswerChange(e.target.value)}
                  placeholder="Напишите ваш ответ (2-5 предложений)..."
                  className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-xl placeholder:text-neutral-600 light:placeholder:text-neutral-400 text-white light:text-neutral-900 light:bg-neutral-100 light:border-neutral-300 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20 transition-all min-h-[120px] resize-none"
                />
              )}
            </div>
          </div>

          {/* Tips */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-xs text-neutral-500">
              💡 <span className="text-neutral-400">Совет:</span> Отвечайте честно. AI анализирует глубину понимания, а не заученные ответы.
            </p>
          </div>
        </div>

        {/* Bottom Button */}
        <div className="flex-shrink-0 p-6 bg-tg-bg/80 backdrop-blur-xl border-t border-white/10 safe-area-bottom">
          <Button
            onClick={handleNext}
            className="w-full h-14 bg-white text-black hover:bg-neutral-100 font-bold text-base rounded-2xl transition-all active:scale-95"
            disabled={!currentAnswer.trim()}
          >
            {isLastQuestion ? 'Завершить тест' : 'Следующий вопрос'}
          </Button>
        </div>
      </div>
    );
  }

  // Fallback loading
  return (
    <div className="min-h-screen bg-tg-bg p-6 flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );
};

// Helper function to get score info
function getScoreInfo(score: number, badgeLevel: BadgeLevel): { color: string; description: string } {
  const levelColors: Record<BadgeLevel, string> = {
    junior: 'text-warning-400',
    middle: 'text-success-400',
    senior: 'text-primary-400',
    lead: 'text-purple-400',
    expert: 'text-purple-400',
  };

  const levelDescriptions: Record<BadgeLevel, string> = {
    junior: 'Уровень подтвержден',
    middle: 'Хороший уровень подтвержден',
    senior: 'Отличный результат! Приоритет в показе',
    lead: 'Выдающийся результат! Вы в топ-5%',
    expert: 'Выдающийся результат! Вы в топ-5%',
  };

  return {
    color: levelColors[badgeLevel] || 'text-neutral-400',
    description: levelDescriptions[badgeLevel] || 'Тест завершен',
  };
}

export default ITQuestScreen;
